import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { CitadelDialogService, CitadelIconService, CitadelPageStyleService, CitadelSystemNotificationService, FooterAction, FooterActionList } from '@citadel/controls';
import { ApplicationService } from './services/application-service.service';

import { CitadelConfigurationService, CitadelErrorLoggingService, CitadelMaintenanceService, CitadelWebService } from '@citadel/core';
import { first, map, mergeMap } from 'rxjs';
import { RegistrationService } from './services/registration-service.service';
import { CitadelThemeService } from '@citadel/styles';
import { RecaptchaService } from './services/recaptcha.service';
import { ComponentFSPDetailDTO } from './models/component-fsp-detail.model';

/** A footer link built locally from a ComponentFSPDetailDTO row's title/url/position. */
interface FooterLinkItem {
  title: string;
  url: string;
  position: number | null;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit {
  private web = inject(CitadelWebService);
  private router = inject(Router);
  private application = inject(ApplicationService);
  private icons = inject(CitadelIconService);
  private config = inject(CitadelConfigurationService);
  private systemNotification = inject(CitadelSystemNotificationService);
  // private overlay = inject(Overlay);
  // private element = inject(ElementRef);
  // private viewContainerRef = inject(ViewContainerRef);
  private colorService: CitadelPageStyleService = inject(CitadelPageStyleService);
  private dialog = inject(CitadelDialogService);
  private maintain = inject(CitadelMaintenanceService);
  private regServ: RegistrationService = inject(RegistrationService);
  private theme = inject(CitadelThemeService);
  private errorLog = inject(CitadelErrorLoggingService);

  private recaptchaService: RecaptchaService = inject(RecaptchaService);

  // Assigned asynchronously (this.application.paragraph.subscribe in ngOnInit) and never read
  // anywhere - unset until that observable emits.
  text?: string;
  emailStr: string = "support@citadel.co.za";
  componentFSPDetailsArray: ComponentFSPDetailDTO[] = [];
  //Banner details
  //------------------------
  description: string = '';
  contactDescriptionBefore: string = '';
  contactDescriptionAfter: string = '';
  email: string = '';
  // Assigned in determineFeatures (via getComponentFSPDetail), which only runs once loadSCPConfig's
  // subscription resolves - unset until then.
  emailObject?: string;
  phoneNumber: string = '';
  //------------------------
  //Footer details
  //------------------------

  footerArray: FooterLinkItem[] = [];

  logo: string = '';
  footerWording: string = '';

  recaptchaToken = '';
  status = '';
  error = '';

  //---------------------

  footerItems: FooterActionList = new FooterActionList();
  protected backgroundClass = 'white'; // alternate will be interpreted as white
  constructor() {
    this.router.events.subscribe(e => {
      if (e instanceof NavigationStart) {
        console.log('SERVER: Routing event: Start:', e.url);
      } else if (e instanceof NavigationEnd) {
        console.log('SERVER: Routing event: End:', e.url);
      }
    });
    this.systemNotification.get().subscribe(({ heading, text }) => this.showNotice(heading, text));
    this.application.paragraph.subscribe(res => {
 this.text = res; 
});

    const correlationID = this.getQueryParamValue('correlationID');
    if (correlationID) {
      this.errorLog.correlationID = correlationID;
    }

    // this.maintain.getMaintenanceCallParameters = settings => this.maintenanceParams.getMaintenanceParams();
    this.config.getGlobalSettingsLoaded().pipe(
      first(),
      // BUG (found adopting strict mode, not fixed here): IAppSettings.rerouteAddress is declared
      // `string`, not a per-hostname map, so this index access can never resolve a real value at
      // runtime as written - it silently returned `undefined` under the old implicit-any indexing
      // too. Casting only to keep that exact (non-)behaviour under strict typing.
      mergeMap(settings => this.maintain.getMaintenancePending().pipe(map(maintenanceEntry => ({ maintenanceEntry, rerouteAddress: (settings.rerouteAddress as unknown as Record<string, string> | undefined)?.[window.location.hostname] })))))
      .subscribe(({ maintenanceEntry, rerouteAddress }) => {
        if (maintenanceEntry) {
          const now = new Date();
          if (maintenanceEntry.endDate >= now && maintenanceEntry.startDate <= now && !!rerouteAddress) {
            window.location.href = rerouteAddress;
          } else if (maintenanceEntry.startDate >= now) {
            this.systemNotification.showNotice('System notice', maintenanceEntry.noticeMessage);
          }
        }
      });

    //this.loadFooter();
    this.theme.loadCurrentTheme();
    this.colorService.getColorScheme().subscribe(scheme => this.backgroundClass = scheme?.background || 'white');
  }
  showNotice(heading: string, text: string) {
    this.dialog.showConfirmation(text, heading, 'Got it', 'bell-ringing', () => { });
    // let overlayRef = this.overlay.create({
    //   disposeOnNavigation: false,
    //   hasBackdrop: false,
    //   scrollStrategy: this.overlay.scrollStrategies.reposition({ autoClose: false }),
    //   maxWidth: '320px',
    //   minWidth: '320px',
    //   positionStrategy: this.overlay.position().flexibleConnectedTo(this.element).withPositions([{
    //     originX: 'end',
    //     originY: 'bottom',
    //     overlayX: 'end',
    //     overlayY: 'bottom',
    //   }])
    // });
    // const popupComponentPortal = new TemplatePortal(this.dropdown, this.viewContainerRef);

    // overlayRef.attach(popupComponentPortal);

    // overlayRef.backdropClick().subscribe(() => {
    //   this.isOpen = false;
    //   this.fields.overlayRef.dispose();
    //   this.fields.overlayRef = null;
    // });
  }

  ngOnInit() {
    this.buildRecaptcha();
    this.registerServices();
    this.registerIcons();
    this.loadSCPConfig();
  }

  buildRecaptcha() {
    this.recaptchaService.initialize().subscribe({
      next: () => {
        this.setStatus('reCAPTCHA initialized successfully!');
      },
      error: (err) => {
        this.setError(`Initialization error: ${err.message}`);
      },
    });
  }

  loadSCPConfig() {
    this.regServ.getComponentFSPDetail()
      .subscribe((details: ComponentFSPDetailDTO[]) => {
        this.componentFSPDetailsArray = details;
        this.determineFeatures(this.componentFSPDetailsArray);

      });

    //Testing only - remove later
    //-------------------------------

    // this.description = 'We are a phone call away if you need any technical support.';
    // this.contactDescriptionBefore = 'Contact us on ';
    // this.contactDescriptionAfter = ' between 08:00 and 16:30 on weekdays, or email ';
    // this.email = 'support@citadel.co.za';
    // this.emailObject = "mailto:support@citadel.co.za?subject=Secure Client Portal enquiry";
    // this.phoneNumber = '+27 86 110 0628';

    // this.footerArray.push({ title: 'Privacy Notice', url: 'https://www.peregrine.gg/wp-content/uploads/2021/03/Privacy-Notice.pdf', position: 1 });
    // this.footerArray.push({ title: 'Conflict of Interest', url: 'https://www.peregrine.gg/wp-content/uploads/2021/03/Conflict-of-Interest.pdf', position: 2 });
    // this.footerArray.push({ title: 'Technical Support', url: 'mailto:support@citadel.co.za', position: 3 });
    // this.footerWording = 'Peregrine Wealth Limited (registration number 39538) and CAM Global Limited (registration number 36784) are licensed by the Guernsey Financial Services Commission. Peregrine Wealth Limited is licensed as a financial services provider in terms of the Financial Advisory and Intermediary Services Act, 2002. Peregrine Wealth Limited and CAM Global Limited are members of the Citadel Group in South Africa.';
    // this.loadFooter();
    //-------------------------------
  }

  determineFeatures(details: ComponentFSPDetailDTO[]) {
    if (details && details.length > 0) {
      //Banner details
      this.description = this.getComponentFSPDetail(details, 7, 5);
      this.contactDescriptionBefore = this.getComponentFSPDetail(details, 7, 6);
      this.contactDescriptionAfter = this.getComponentFSPDetail(details, 7, 7);
      this.phoneNumber = this.getComponentFSPDetail(details, 7, 8);
      this.emailObject = this.getComponentFSPDetail(details, 7, 14);
      this.email = this.getComponentFSPDetail(details, 7, 9);

      //Footer details
      let footerUrl = this.getComponentFSPDetail(details, 1, 10);

      let fIndx = details.findIndex(d => d.componentFSPDetailSectionL === 1 && d.paramKeyL === 1);
      if (fIndx !== -1) {
        this.footerArray.push({ title: details[fIndx].paramValue, url: footerUrl, position: details[fIndx].position });
      }
      
      //Conflict of interest (Citadel)
      footerUrl = '';
      fIndx = details.findIndex(d => d.componentFSPDetailSectionL === 1 && d.paramKeyL === 19);
      if (fIndx !== -1) {
        footerUrl = details[fIndx].paramValue;
      }

      //Conflict of interest (Citadel)
      fIndx = details.findIndex(d => d.componentFSPDetailSectionL === 1 && d.paramKeyL === 2);
      if (fIndx !== -1) {
        this.footerArray.push({ title: details[fIndx].paramValue, url: footerUrl, position: details[fIndx].position });

      }

      //Terms and Conditions  
      footerUrl = this.getComponentFSPDetail(details, 1, 11);

      fIndx = details.findIndex(d => d.componentFSPDetailSectionL === 1 && d.paramKeyL === 3);
      if (fIndx !== -1) {
        this.footerArray.push({ title: details[fIndx].paramValue, url: footerUrl, position: details[fIndx].position });
      }

      //Technical Support (PWL) url
      footerUrl = '';
      fIndx = details.findIndex(d => d.componentFSPDetailSectionL === 1 && d.paramKeyL === 14);
      if (fIndx !== -1) {
        footerUrl = details[fIndx].paramValue;
      }

      //Technical Support (PWL)
      fIndx = details.findIndex(d => d.componentFSPDetailSectionL === 1 && d.paramKeyL === 12);
      if (fIndx !== -1) {
        this.footerArray.push({ title: details[fIndx].paramValue, url: footerUrl, position: details[fIndx].position });
      }

      this.logo = this.getComponentFSPDetail(details, 1, 4);
      this.footerWording = this.getComponentFSPDetail(details, 1, 13);
    }
    this.loadFooter();
  }

  getComponentFSPDetail(details: ComponentFSPDetailDTO[], section: number, key: number): string {
    let result: string = '';
    const fIndx = details.findIndex(d => d.componentFSPDetailSectionL === section && d.paramKeyL === key);
    if (fIndx !== -1) {
      result = details[fIndx].paramValue;

    }
    return result;
  }

  loadFooter() {
    // this.footerItems.logo = 'logo';
    // this.footerItems.copyrightName = `© Citadel ${(new Date()).getFullYear()}`;
    // this.footerItems.useLogo = true;

    // let footerItem: FooterAction = new FooterAction();
    // footerItem.url = 'https://www.citadel.co.za/privacy-notice/';
    // footerItem.title = 'Privacy Notice';

    // this.footerItems.actions.push(footerItem);

    // footerItem = new FooterAction()
    // footerItem.url = 'mailto:compliance@citadel.co.za';
    // footerItem.title = 'Conflict of Interest';

    // this.footerItems.actions.push(footerItem);

    // footerItem = new FooterAction()
    // footerItem.url = 'https://www.citadel.co.za/terms-and-conditions/';
    // footerItem.title = 'Terms and Conditions';

    // this.footerItems.actions.push(footerItem);

    //Footer
    let footerItem: FooterAction = new FooterAction();
    const footerItemsTemp: FooterActionList = new FooterActionList();

    footerItemsTemp.logo = 'logo';
    if (this.logo && this.logo !== '') {
      footerItemsTemp.copyrightName = `© ${this.logo} ${new Date().getFullYear()}`;
      footerItemsTemp.useLogo = true;
    }

    const tempArray: FooterAction[] = [];
    for (const item of this.footerArray.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))) {
      footerItem = new FooterAction();
      footerItem.title = item.title;
      footerItem.url = item.url;
      tempArray.push(footerItem);

    }

    footerItemsTemp.actions.push(...tempArray);

    this.footerItems = footerItemsTemp;

  }

  private registerIcons() {
    this.icons.addIconLibrary('', 'assets/secure-portal-icons.svg');
    this.icons.addIconLibrary('calc', 'assets/calculator-and-contact-icons.svg');
    this.icons.addIconLibrary('dash', 'assets/dashboard-icons.svg');
    this.icons.addIconLibrary('flag', 'assets/flag-icons.svg');
  }

  private registerServices() {
    this.web.addServicesFromConfig(this.config.getConfig(),
      { name: 'Citadel.Auth.User', serviceConfigName: 'Services:Citadel.Framework.Permission', controllerName: 'User' },
      { name: 'Citadel.Menu', serviceConfigName: 'Services:Citadel.Menu', controllerName: 'Menu' },
      { name: 'Citadel.Lookups', serviceConfigName: 'Services:Citadel.Lookups', controllerName: 'Lookup' },
      { name: 'Tyrus.Entity.Search', serviceConfigName: 'Services:Tyrus.Entity.Integration', controllerName: 'Client' },
      { name: 'Tyrus.Entity.Sharing', serviceConfigName: 'Services:Tyrus.Entity.Integration', controllerName: 'Staff' },
      { name: 'Tyrus.Entity.Sharing', serviceConfigName: 'Services:Tyrus.Entity.Integration', controllerName: 'Staff' },
      { name: 'crm.Party', serviceConfigName: 'Services:Citadel.CRM.Service', controllerName: 'Party' },
      { name: 'PushNotifications', serviceConfigName: 'Services:Citadel.Notification.Service', controllerName: 'Push' },
      { name: 'SCP.Client', serviceConfigName: 'Services:Citadel.SecurePortal.Admin.API', controllerName: 'Client' },
    );
  }

  setStatus(text: string) {
    this.status = text;
    setTimeout(() => this.status = '', 3000);
  }
  setError(text: string) {
    this.error = text;
  }

  private getQueryParamValue(key: string): string | null {
    let search = window.location.search || '';

    for (let attempt = 0; attempt < 3; attempt++) {
      const value = new URLSearchParams(search).get(key);
      if (value) {
        return value;
      }

      const decodedSearch = this.tryDecodeURIComponent(search);
      if (decodedSearch === search) {
        break;
      }

      search = decodedSearch;
    }

    return null;
  }

  private tryDecodeURIComponent(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

}
